import { useState, useEffect, FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { authApi } from '../services/api';
import { AuthBrand } from '../components/AuthBrand';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError('Invalid password reset link.');
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) return setError('Password must be at least 8 characters.');
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    if (!token) return;
    setIsLoading(true);
    try {
      await authApi.resetPasswordWithToken(token, newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="relative w-full max-w-md animate-fade-in">
        <AuthBrand />
        <div className="bg-background-secondary border border-border rounded-xl p-8 shadow-xl">
          {success ? (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-accent mx-auto mb-4" />
              <h1 className="text-xl font-semibold mb-3">Password Reset Successful</h1>
              <p className="text-sm text-foreground-secondary">Redirecting to sign in...</p>
            </div>
          ) : !token ? (
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-error mx-auto mb-4" />
              <h1 className="text-xl font-semibold mb-3">Invalid Reset Link</h1>
              <Link to="/forgot-password" className="btn-primary w-full py-2.5 mb-4 block text-center">Request New Link</Link>
              <Link to="/login" className="text-accent inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <Lock className="w-5 h-5 text-accent" />
                <h1 className="text-xl font-semibold">Set New Password</h1>
              </div>
              {error && <div className="mb-6 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">{error}</div>}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="label">New Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="input pr-10" minLength={8} required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label">Confirm Password</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="input" minLength={8} required />
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full py-2.5">
                  {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Resetting...</> : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
